import { useAccount } from '@app/src/context/AccountContext'
import { shouldConfirmApplySelfOwnerRemoval } from '@app/src/utils/providerOwnerUsersUtils'
import { useConfirm } from 'material-ui-confirm'
import { useIntl } from 'react-intl'
import { useTheme } from '@mui/material'
import { useOwnerRestriction } from './useOwnerRestriction'

type OwnerRemovalConfirmationOptions = {
  originalOwnerIds: number[]
  newOwnerIds: number[]
  providerId?: number
  providerCount?: number
}

/**
 * Custom hook for confirming owner removal when owner-restricted users remove themselves as owners.
 *
 * This hook encapsulates the logic for detecting when an owner-restricted user is about to remove
 * themselves as an owner and prompting for confirmation before proceeding.
 *
 * @example
 * const { confirmOwnerChange } = useOwnerRemovalConfirmation()
 *
 * const onSubmit = async (values) => {
 *   const confirmed = await confirmOwnerChange({
 *     originalOwnerIds: provider.ownerUserIds ?? [],
 *     newOwnerIds: values.ownerUserIds ?? [],
 *     providerId: provider.id,
 *     providerCount: 1,
 *   })
 *
 *   if (!confirmed) return
 *   // Proceed with save
 * }
 */
export const useOwnerRemovalConfirmation = () => {
  const { account } = useAccount()
  const { accountIsOwnerRestricted } = useOwnerRestriction()
  const confirm = useConfirm()
  const { formatMessage } = useIntl()
  const theme = useTheme()

  /**
   * Checks if the owner change would remove the current user as owner and prompts for confirmation if needed.
   *
   * @param options Configuration options including original/new owner IDs and provider info
   * @returns Promise that resolves to true if user confirmed or no confirmation needed, false if user cancelled
   */
  const confirmOwnerChange = async (options: OwnerRemovalConfirmationOptions): Promise<boolean> => {
    const { originalOwnerIds, newOwnerIds, providerId = 0, providerCount = 1 } = options

    // Check if confirmation is needed using the utility function
    const shouldConfirm = shouldConfirmApplySelfOwnerRemoval(
      [{ providerId, ownerUserIds: originalOwnerIds }],
      [{ providerId, ownerUserIds: newOwnerIds }],
      account?.user?.id,
      accountIsOwnerRestricted,
    )

    if (!shouldConfirm) {
      return true
    }

    // Show confirmation dialog with elevated z-index to appear above drawer
    try {
      await confirm({
        title: formatMessage({ id: 'general.areYouSure' }),
        description: formatMessage({ id: 'schemas.provider.ownerRemoval.confirmation' }, { count: providerCount }),
        dialogProps: {
          style: {
            zIndex: theme.zIndex.modal + 2,
          },
        },
      })
      return true
    } catch {
      // User cancelled
      return false
    }
  }

  return { confirmOwnerChange }
}
